import React, { useMemo, useState, useEffect } from "react";
import "./orderDetails.css";
import jsPDF from 'jspdf';
import seal from "../../assets/seal.png";
import {
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiClock,
  FiMapPin,
  FiFileText,
  FiPackage,
  FiX,
} from "react-icons/fi";
import { BiExport } from "react-icons/bi";

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});
const formatINR = (value) => inrFormatter.format(value ?? 0);

// Status configuration
const STATUS_STEPS = [
  { key: "pending", label: "Order Placed", apiValue: "PENDING", description: "An order has been placed.", color: "#6b7280" },
  { key: "processing", label: "Processing", apiValue: "PROCESSING", description: "Seller has processed your order.", color: "#f59e0b" },
  { key: "packed", label: "Packed", apiValue: "PACKED", description: "Order has been packed.", color: "#8b5cf6" },
  { key: "shipped", label: "Shipped", apiValue: "SHIPPED", description: "Order is on the way.", color: "#3b82f6" },
  { key: "delivered", label: "Delivered", apiValue: "DELIVERED", description: "Order has been delivered.", color: "#10b981" },
];

const mapStatusToKey = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "processing") return "processing";
  if (s === "packed") return "packed";
  if (s === "shipped" || s === "shipping") return "shipped";
  if (s === "delivered") return "delivered";
  return "pending";
};

// Payment Status configuration
const PAYMENT_STATUS_STEPS = [
  {
    key: "unpaid",
    label: "Unpaid",
    apiValue: "UNPAID",
    description: "Payment has not been received yet.",
    color: "#ef4444",
  },
  {
    key: "practicallyPaid",
    label: "Partially Paid",
    apiValue: "PRACTICALLY_PAID",
    description: "Payment will be adjusted / partly settled.",
    color: "#f59e0b",
  },
  {
    key: "paid",
    label: "Paid",
    apiValue: "PAID",
    description: "Full payment received.",
    color: "#10b981",
  },
];




// Map backend payment status to our internal keys
const mapPaymentStatusToKey = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "paid") return "paid";
  if (s === "practically paid" || s === "practically_paid") return "practicallyPaid";
  return "unpaid"; // default
};

// Helper function to get item property
const getItemProperty = (item, ...possibleKeys) => {
  if (!item) return null;
  for (const key of possibleKeys) {
    if (key.includes('.')) {
      const parts = key.split('.');
      let value = item;
      for (const part of parts) {
        value = value?.[part];
      }
      if (value !== undefined && value !== null && value !== '') return value;
    } else if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      return item[key];
    }
  }
  return null;
};

// Convert number to words (Indian numbering system, rupees only)
function numberToWordsIndian(num) {
  num = Math.round(Number(num) || 0);
  if (num === 0) return 'Zero';

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const toWordsBelowThousand = (n) => {
    let str = '';
    if (n > 99) {
      str += ones[Math.floor(n / 100)] + ' Hundred ';
      n = n % 100;
    }
    if (n > 19) {
      str += tens[Math.floor(n / 10)] + ' ';
      n = n % 10;
    }
    if (n > 0) {
      str += ones[n] + ' ';
    }
    return str.trim();
  };

  let result = '';

  const crore = Math.floor(num / 10000000);
  if (crore > 0) {
    result += toWordsBelowThousand(crore) + ' Crore ';
    num = num % 10000000;
  }

  const lakh = Math.floor(num / 100000);
  if (lakh > 0) {
    result += toWordsBelowThousand(lakh) + ' Lakh ';
    num = num % 100000;
  }

  const thousand = Math.floor(num / 1000);
  if (thousand > 0) {
    result += toWordsBelowThousand(thousand) + ' Thousand ';
    num = num % 1000;
  }

  const hundredAndBelow = num;
  if (hundredAndBelow > 0) {
    result += toWordsBelowThousand(hundredAndBelow) + ' ';
  }

  return result.trim();
}

// For PDF
const formatAmountForPdf = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Find image URL
const findImageUrl = (item) => {
  if (!item) return null;
  const imageFields = ['image', 'imageUrl', 'image_url', 'url', 'productImage', 'product_image', 'thumbnail', 'img', 'picture', 'photo', 'src'];
  for (const field of imageFields) {
    if (item[field] && typeof item[field] === 'string' && item[field].trim() !== '') {
      return item[field];
    }
  }
  for (const [key, value] of Object.entries(item)) {
    if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://')) &&
      (value.includes('.jpg') || value.includes('.jpeg') || value.includes('.png') || value.includes('.webp') || value.includes('.gif') || value.includes('blob'))) {
      return value;
    }
  }
  return null;
};

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-icon">
        {type === 'success' ? <FiCheck /> : <FiX />}
      </div>
      <span className="toast-message">{message}</span>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
};

// Tax class rates for frontend calculation (fallback)
const TAX_CLASS_RATES = {
  'GST': 18,
  'tax_free': 0,
  'GST_5': 5,
  'GST_12': 12,
  'GST_18': 18,
  'GST_28': 28
};

const getGstRate = (taxClass) => TAX_CLASS_RATES[taxClass] || 0;

const OrderDetails = ({ order, onBack, onStatusChange, onPaymentStatusChange }) => {
  const safeOrder = order || {};

  // Toast state
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Debug: Log order data (remove in production)
  useEffect(() => {
    if (order) {
      console.log("📦 Order Details received:", {
        orderId: order.orderId || order.order_id,
        subtotal: order.subtotal,
        gstAmount: order.gstAmount,
        gstPercentage: order.gstPercentage,
        vatAmount: order.vatAmount,
        vatPercentage: order.vatPercentage,
        totalAmount: order.totalAmount,
        itemsCount: order.items?.length,
        items: order.items?.slice(0, 2).map(i => ({
          sku: i.sku,
          subtotal: i.subtotal,
          gstRate: i.gstRate,
          gstAmount: i.gstAmount,
          vatPercentage: i.vatPercentage,
          vatAmount: i.vatAmount
        }))
      });
    }
  }, [order]);

  // Get items
  const items = safeOrder.items || safeOrder.orderItems || safeOrder.products || [];

  // Get customer info - UPDATED to read from backend correctly
  const customer = safeOrder.customer || safeOrder.user || {};

  const customerName =
    safeOrder.customerName ||
    safeOrder.panchayathName ||
    customer.name ||
    customer.fullName ||
    "—";

  const customerEmail =
    safeOrder.customerEmail ||
    safeOrder.panchayathEmail ||
    customer.email ||
    "—";

  const customerPhone =
    safeOrder.customerPhone ||
    customer.phone ||
    customer.mobile ||
    "—";

  const customerPincode =
    safeOrder.panchayathPincode ||
    customer.pincode ||
    customer.pin_code ||
    customer.postalCode ||
    "—";

  // Payment status state (frontend only for now)
  const [paymentStatusKey, setPaymentStatusKey] = useState(
    mapPaymentStatusToKey(
      safeOrder.paymentStatus || safeOrder.payment_status || "UNPAID"
    )
  );

  useEffect(() => {
    const newPaymentStatusKey = mapPaymentStatusToKey(
      safeOrder.paymentStatus || safeOrder.payment_status || "UNPAID"
    );
    setPaymentStatusKey(newPaymentStatusKey);
  }, [safeOrder.paymentStatus, safeOrder.payment_status]);

  // Status state
  const [statusKey, setStatusKey] = useState(
    mapStatusToKey(safeOrder.status || safeOrder.order_status || safeOrder.orderStatus || "PENDING")
  );
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Payment status update flag
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = useState(false);

  useEffect(() => {
    const newStatusKey = mapStatusToKey(safeOrder.status || safeOrder.order_status || safeOrder.orderStatus || "PENDING");
    setStatusKey(newStatusKey);
  }, [safeOrder.status, safeOrder.order_status, safeOrder.orderStatus]);

  useEffect(() => {
    const rawHistory = safeOrder.statusHistory || safeOrder.status_history;

    if (!rawHistory) {
      setStatusUsers({});
      return;
    }

    // rawHistory is an object: { PENDING: { updatedBy, updatedAt }, ... }
    if (!Array.isArray(rawHistory) && typeof rawHistory === "object") {
      const map = {};
      Object.entries(rawHistory).forEach(([status, meta]) => {
        const key = mapStatusToKey(status);
        if (meta && meta.updatedBy) {
          map[key] = meta.updatedBy;
        }
      });
      setStatusUsers(map);
      return;
    }

    // Optional: if in future you ever switch to array form [{status, updatedBy, ...}]
    if (Array.isArray(rawHistory)) {
      const map = {};
      rawHistory.forEach((entry) => {
        const key = mapStatusToKey(entry.status);
        if (entry.updatedBy) {
          map[key] = entry.updatedBy;
        }
      });
      setStatusUsers(map);
    }
  }, [safeOrder.statusHistory, safeOrder.status_history]);



  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(""); // NEW

  useEffect(() => {
    const defaultInvoiceNo =
      safeOrder.invoiceNumber ||
      safeOrder.orderNumber ||
      safeOrder.id ||
      safeOrder.order_id ||
      safeOrder.orderId ||
      "";

    setInvoiceNumber(defaultInvoiceNo ? String(defaultInvoiceNo) : "");

    // NEW: default date (yyyy-mm-dd for <input type="date">)
    const rawDate =
      safeOrder.invoiceDate ||
      safeOrder.date ||
      (safeOrder.createdAt ? safeOrder.createdAt.split("T")[0] : "") ||
      "";

    setInvoiceDate(rawDate ? rawDate.substring(0, 10) : "");
  }, [
    safeOrder.invoiceNumber,
    safeOrder.orderNumber,
    safeOrder.id,
    safeOrder.order_id,
    safeOrder.orderId,
    safeOrder.invoiceDate,
    safeOrder.date,
    safeOrder.createdAt,
  ]);


  // NEW: status -> last updatedBy name
  const [statusUsers, setStatusUsers] = useState({});

  const activeIndex = useMemo(() => {
    const i = STATUS_STEPS.findIndex((s) => s.key === statusKey);
    return i === -1 ? 0 : i;
  }, [statusKey]);

  const currentStatusColor = useMemo(() => {
    const step = STATUS_STEPS.find(s => s.key === statusKey);
    return step?.color || "#6b7280";
  }, [statusKey]);

  const paymentActiveIndex = useMemo(() => {
    const i = PAYMENT_STATUS_STEPS.findIndex((s) => s.key === paymentStatusKey);
    return i === -1 ? 0 : i;
  }, [paymentStatusKey]);

  const currentPaymentStatusColor = useMemo(() => {
    const step = PAYMENT_STATUS_STEPS.find((s) => s.key === paymentStatusKey);
    return step?.color || "#6b7280";
  }, [paymentStatusKey]);

  // ============================================
  // CALCULATE SUBTOTAL, GST, VAT COMPLETELY SEPARATELY
  // ============================================

  const taxBreakdown = useMemo(() => {
    // Try to get pre-calculated values from backend first
    const backendSubtotal = safeOrder.subtotal ?? safeOrder.subTotal;
    const backendGstAmount = safeOrder.gstAmount ?? safeOrder.gst_amount;
    const backendGstPercentage = safeOrder.gstPercentage ?? safeOrder.gst_percentage;
    const backendVatAmount = safeOrder.vatAmount ?? safeOrder.vat_amount;
    const backendVatPercentage = safeOrder.vatPercentage ?? safeOrder.vat_percentage;
    const backendTotal = safeOrder.grandTotal ?? safeOrder.total ?? safeOrder.totalAmount ?? safeOrder.total_amount;
    const backendShipping = safeOrder.shippingRate ?? safeOrder.shipping ?? safeOrder.shippingCost ?? 0;

    console.log("📊 Tax Breakdown - Backend values:", {
      backendSubtotal,
      backendGstAmount,
      backendGstPercentage,
      backendVatAmount,
      backendVatPercentage,
      backendTotal
    });

    // If backend provided gstAmount OR vatAmount, use backend values
    if (backendGstAmount !== undefined || backendVatAmount !== undefined) {
      const sub = Number(backendSubtotal) || 0;
      const gst = Number(backendGstAmount) || 0;
      const vat = Number(backendVatAmount) || 0;
      const shipping = Number(backendShipping) || 0;

      return {
        subtotal: sub,
        gstAmount: gst,
        gstPercentage: backendGstPercentage || (sub > 0 && gst > 0 ? Math.round((gst / sub) * 100) : 0),
        vatAmount: vat,
        vatPercentage: backendVatPercentage || (sub > 0 && vat > 0 ? Math.round((vat / sub) * 100) : 0),
        shippingRate: shipping,
        grandTotal: Number(backendTotal) || (sub + gst + vat + shipping)
      };
    }

    // Fallback: Calculate from items
    console.log("📊 Tax Breakdown - Calculating from items...");

    let calcSubtotal = 0;
    let calcGstAmount = 0;
    let calcVatAmount = 0;

    const gstRatesUsed = new Map();
    const vatRatesUsed = new Map();

    items.forEach(item => {
      // Get item subtotal
      const itemSubtotalValue = getItemProperty(item, 'subtotal');
      const price = Number(getItemProperty(item, 'price', 'base_price', 'basePrice', 'unitPrice') || 0);
      const qty = Number(getItemProperty(item, 'qty', 'quantity', 'count') || 1);
      const itemSubtotal = itemSubtotalValue ? Number(itemSubtotalValue) : price * qty;

      calcSubtotal += itemSubtotal;

      // ===== GST (from tax_class) =====
      const itemGstAmount = Number(getItemProperty(item, 'gstAmount', 'gst_amount', 'item_gst_amount') || 0);
      const itemGstRate = Number(getItemProperty(item, 'gstRate', 'gst_rate') || 0);
      const taxClass = getItemProperty(item, 'taxClass', 'tax_class');

      if (itemGstAmount > 0) {
        // Use pre-calculated GST from backend
        calcGstAmount += itemGstAmount;
        const rateKey = itemGstRate > 0 ? itemGstRate : 'unknown';
        const existing = gstRatesUsed.get(rateKey) || 0;
        gstRatesUsed.set(rateKey, existing + itemGstAmount);
      } else if (taxClass && taxClass !== 'tax_free') {
        // Calculate GST from tax class
        const rate = getGstRate(taxClass);
        if (rate > 0) {
          const gstAmt = (itemSubtotal * rate) / 100;
          calcGstAmount += gstAmt;
          const existing = gstRatesUsed.get(rate) || 0;
          gstRatesUsed.set(rate, existing + gstAmt);
        }
      }

      // ===== VAT (from vat_percentage - custom values, SEPARATE from GST) =====
      const itemVatAmount = Number(getItemProperty(item, 'vatAmount', 'vat_amount', 'item_vat_amount') || 0);
      const itemVatPct = Number(getItemProperty(item, 'vatPercentage', 'vat_percentage') || 0);

      if (itemVatAmount > 0) {
        // Use pre-calculated VAT from backend
        calcVatAmount += itemVatAmount;
        const rateKey = itemVatPct > 0 ? itemVatPct : 'unknown';
        const existing = vatRatesUsed.get(rateKey) || 0;
        vatRatesUsed.set(rateKey, existing + itemVatAmount);
      } else if (itemVatPct > 0) {
        // Calculate VAT from percentage
        const vatAmt = (itemSubtotal * itemVatPct) / 100;
        calcVatAmount += vatAmt;
        const existing = vatRatesUsed.get(itemVatPct) || 0;
        vatRatesUsed.set(itemVatPct, existing + vatAmt);
      }
    });

    // Determine display percentages
    const gstRates = Array.from(gstRatesUsed.keys()).filter(r => r !== 'unknown');
    const vatRates = Array.from(vatRatesUsed.keys()).filter(r => r !== 'unknown');

    let displayGstPct = 0;
    if (gstRates.length === 1) {
      displayGstPct = gstRates[0];
    } else if (gstRates.length > 1) {
      displayGstPct = 'Mixed';
    }

    let displayVatPct = 0;
    if (vatRates.length === 1) {
      displayVatPct = vatRates[0];
    } else if (vatRates.length > 1) {
      displayVatPct = 'Mixed';
    }

    const shipping = Number(backendShipping) || 0;

    const result = {
      subtotal: Math.round(calcSubtotal * 100) / 100,
      gstAmount: Math.round(calcGstAmount * 100) / 100,
      gstPercentage: displayGstPct,
      vatAmount: Math.round(calcVatAmount * 100) / 100,
      vatPercentage: displayVatPct,
      shippingRate: shipping,
      grandTotal: Math.round((calcSubtotal + calcGstAmount + calcVatAmount + shipping) * 100) / 100
    };

    console.log("📊 Tax Breakdown - Calculated result:", result);
    return result;
  }, [safeOrder, items]);

  const { subtotal, gstAmount, gstPercentage, vatAmount, vatPercentage, shippingRate, grandTotal } = taxBreakdown;

  const productsCount = items.length;

  // Get shipping address
  const shippingAddress = useMemo(() => {
    // Check formatted address from backend
    if (safeOrder.shippingAddressFormatted && safeOrder.shippingAddressFormatted.trim()) {
      return safeOrder.shippingAddressFormatted;
    }

    if (typeof safeOrder.shippingAddress === 'string' && safeOrder.shippingAddress.trim() && safeOrder.shippingAddress !== '-') {
      return safeOrder.shippingAddress;
    }

    // If shipping address is an object, build it
    if (typeof safeOrder.shippingAddress === 'object' && safeOrder.shippingAddress) {
      const addr = safeOrder.shippingAddress;
      const parts = [
        addr.street || addr.addressLine1,
        addr.landmark,
        addr.panchayat || addr.panchayath,
        addr.taluk,
        addr.city || addr.district,
        addr.state,
        addr.pincode || addr.postalCode
      ].filter(Boolean);

      if (parts.length > 0) {
        return parts.join(', ');
      }
    }

    if (typeof customer.address === 'string' && customer.address.trim() && customer.address !== '-') {
      return customer.address;
    }

    const parts = [
      customer.landmark,
      customer.panchayat,
      customer.taluk,
      customer.district,
      customer.state,
      customer.pincode
    ].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(', ');
    }

    return "No address provided";
  }, [safeOrder.shippingAddress, safeOrder.shippingAddressFormatted, customer]);

  // Handle status change
  const handleStatusChange = async (e) => {
    const newStatusKey = e.target.value;
    const step = STATUS_STEPS.find((s) => s.key === newStatusKey);
    const apiStatus = step?.apiValue || "PENDING";
    const orderId = safeOrder.id || safeOrder.order_id || safeOrder.orderId;

    if (!orderId) {
      addToast("Order ID not found", "error");
      return;
    }

    setUpdatingStatus(true);

    try {
      let updatedOrder = null;
      const currentUserName = localStorage.getItem("userName") || "Unknown";

      if (onStatusChange) {
        // IMPORTANT: pass userName to parent
        updatedOrder = await onStatusChange(orderId, apiStatus, currentUserName);
      }

      setStatusKey(newStatusKey);

      // Rebuild map from backend response
      const rawHistory =
        updatedOrder?.statusHistory || updatedOrder?.status_history;

      if (rawHistory && typeof rawHistory === "object") {
        const map = {};
        Object.entries(rawHistory).forEach(([status, meta]) => {
          const key = mapStatusToKey(status);
          if (meta && meta.updatedBy) {
            map[key] = meta.updatedBy;
          }
        });
        setStatusUsers(map);
      }

      addToast(`Status updated to ${step?.label}`, "success");
    } catch (err) {
      console.error("Status update error:", err);
      addToast(err.message || "Failed to update status", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle payment status change
  const handlePaymentStatusChange = async (e) => {
    const newStatusKey = e.target.value;
    const step = PAYMENT_STATUS_STEPS.find((s) => s.key === newStatusKey);
    const apiStatus = step?.apiValue || "UNPAID";
    const orderId =
      safeOrder.id ||
      safeOrder.order_id ||
      safeOrder.orderId ||
      safeOrder.orderNumber;

    if (!orderId) {
      addToast("Order ID not found", "error");
      return;
    }

    setUpdatingPaymentStatus(true);

    try {
      const currentUserName = localStorage.getItem("userName") || "Unknown";

      if (typeof onPaymentStatusChange === "function") {
        // Call parent -> backend
        const updatedOrder = await onPaymentStatusChange(
          orderId,
          apiStatus,
          currentUserName
        );

        // Normalize from backend response
        const backendKey = mapPaymentStatusToKey(
          updatedOrder?.paymentStatus || updatedOrder?.payment_status || apiStatus
        );
        setPaymentStatusKey(backendKey);
      } else {
        // Frontend-only: just update local state
        setPaymentStatusKey(newStatusKey);
      }

      addToast(`Payment status updated to ${step?.label}`, "success");
    } catch (err) {
      console.error("Payment status update error:", err);
      addToast(err.message || "Failed to update payment status", "error");
    } finally {
      setUpdatingPaymentStatus(false);
    }
  };

  // Export order
  const handleExportOrder = () => {
    const orderId = safeOrder.orderNumber || safeOrder.id || safeOrder.orderId;

    const orderData = {
      orderNumber: orderId,
      date: safeOrder.dateLabel || safeOrder.createdAt,
      customer: {
        name: customerName,
        email: customerEmail,
        phone: customerPhone,
        pincode: customerPincode,
        address: shippingAddress
      },
      items: items.map((item) => ({
        name: getItemProperty(item, 'name', 'product_name', 'productName'),
        sku: getItemProperty(item, 'sku'),
        qty: getItemProperty(item, 'qty', 'quantity') || 1,
        price: getItemProperty(item, 'price', 'base_price', 'basePrice') || 0,
        subtotal: getItemProperty(item, 'subtotal') || 0,
        taxClass: getItemProperty(item, 'taxClass', 'tax_class'),
        gstRate: getItemProperty(item, 'gstRate', 'gst_rate') || 0,
        gstAmount: getItemProperty(item, 'gstAmount', 'gst_amount') || 0,
        vatPercentage: getItemProperty(item, 'vatPercentage', 'vat_percentage') || 0,
        vatAmount: getItemProperty(item, 'vatAmount', 'vat_amount') || 0,
      })),
      subtotal,
      gst: { amount: gstAmount, percentage: gstPercentage },
      vat: { amount: vatAmount, percentage: vatPercentage },
      shipping: shippingRate,
      grandTotal,
      status: STATUS_STEPS.find((s) => s.key === statusKey)?.label,
    };

    const blob = new Blob([JSON.stringify(orderData, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `order-${orderId}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
    addToast("Order exported successfully!", "success");
  };


  // Generate PDF invoice exactly matching the uploaded pattern
  const handleInvoice = async () => {
    const rawOrderId =
      safeOrder.orderNumber ||
      safeOrder.id ||
      safeOrder.order_id ||
      safeOrder.orderId ||
      "";

    const effectiveInvoiceNo =
      (invoiceNumber && invoiceNumber.trim()) ||
      rawOrderId ||
      "N/A";

    if (!effectiveInvoiceNo || effectiveInvoiceNo === "N/A") {
      addToast("Invoice number not found", "error");
      return;
    }

    try {
      const doc = new jsPDF("p", "mm", "a4");

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const marginX = 10;
      const marginY = 10;
      const contentWidth = pageWidth - marginX * 2;

      /* =========================
         TOP + BOTTOM MAIN BOXES
      ========================== */
      const topBoxHeight = 75; // Slightly increased to accommodate email line
      const topBoxBottomY = marginY + topBoxHeight;
      const bottomBoxY = topBoxBottomY;
      const bottomBoxHeight = pageHeight - marginY - bottomBoxY;

      doc.setLineWidth(0.2);
      doc.rect(marginX, marginY, contentWidth, topBoxHeight);
      doc.rect(marginX, bottomBoxY, contentWidth, bottomBoxHeight);

      /* =========================
         HEADER
      ========================== */
      const headerTopY = marginY + 3;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(220, 0, 0);
      doc.text("SHUBHA GRAPHICS", marginX + 2, headerTopY + 6);

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 255);

      const companyLines = [
        "MFR.& SUPPLIER OF LEDGER BOOK, OFFICE FILES & STATIONERY ITEMS.",
        "S.No. 138/58, KUNJIBETTU, BALIARAPADY MIYAR VILLAGE & POST,",
        "KARKALA TQ - 574107 UDUPI DIST.",
        "Mobile: 9880513236 / 9449645510",
        "Email: shubhagraphicskkl@gmail.com",
      ];

      let cy = headerTopY + 11;
      companyLines.forEach((line) => {
        doc.text(line, marginX + 2, cy);
        cy += 3.5;
      });

      // "Tax Invoice" and header line - MOVED LOWER
      const headerLineY = marginY + 32; // Increased from 28 to 32
      doc.setFont("times", "italic");
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Tax Invoice", pageWidth / 2, headerLineY - 2, { align: "center" });

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.line(marginX, headerLineY, marginX + contentWidth, headerLineY);

      /* =========================
         "TO" SECTION
      ========================== */
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      let toY = headerLineY + 5;
      doc.text("To:", marginX + 2, toY);
      if (customerName && customerName !== "—") {
        doc.setFont("helvetica", "bold");
        doc.text(customerName, marginX + 12, toY);
        doc.setFont("helvetica", "normal");
        toY += 4;
      }

      if (shippingAddress && shippingAddress !== "No address provided") {
        const addr = doc.splitTextToSize(shippingAddress, 75);
        doc.text(addr, marginX + 12, toY);
        toY += addr.length * 3.5;
      }

      if (customerPhone && customerPhone !== "—") {
        doc.text("Mob:", marginX + 2, toY + 2);
        doc.text(String(customerPhone), marginX + 12, toY + 2);
      }

      /* =========================
         RIGHT INFO BLOCK (TIGHTER SPACING)
      ========================== */
      const infoX = marginX + contentWidth * 0.58;
      doc.line(infoX, headerLineY, infoX, topBoxBottomY);

      // Use manual date if provided; fallback to createdAt or today
      const rawInvoiceDate =
        (invoiceDate && invoiceDate.trim()) ||
        (safeOrder.createdAt ? safeOrder.createdAt.split("T")[0] : "") ||
        new Date().toISOString().slice(0, 10);

      // Format to dd/mm/yyyy for PDF
      const formattedInvoiceDate = rawInvoiceDate.includes("-")
        ? (() => {
          const [y, m, d] = rawInvoiceDate.split("-");
          return `${d}/${m}/${y}`;
        })()
        : rawInvoiceDate;

      const infoRows = [
        ["Invoice No.", effectiveInvoiceNo],
        ["Date:", formattedInvoiceDate],
        ["GSTIN:", "29AWDPP3227B1ZR"],
        ["PAN:", "AWDPP3227B"],
        ["Transport By:", safeOrder.transportBy || ""],
        ["Party GSTIN:", customer?.gstin || ""],
      ];

      let iy = headerLineY + 5;
      doc.setFontSize(8);
      infoRows.forEach(([l, v]) => {
        doc.setFont("helvetica", "normal");
        doc.text(l, infoX + 2, iy);
        if (v) {
          doc.setFont("helvetica", "bold");
          doc.text(v, pageWidth - marginX - 2, iy, { align: "right" });
        }
        iy += 4; // Reduced from 5 to 4
      });

      /* =========================
         ITEMS TABLE
      ========================== */
      const tableX = marginX;
      const tableY = topBoxBottomY + 2;
      const rowHeight = 7;
      const colWidths = [8, 72, 22, 18, 14, 20, 26];

      const itemsBoxHeight = 100;
      doc.rect(tableX, tableY, contentWidth, itemsBoxHeight);

      // vertical grid lines
      let x = tableX;
      colWidths.forEach((w) => {
        doc.line(x, tableY, x, tableY + itemsBoxHeight);
        x += w;
      });
      doc.line(tableX + contentWidth, tableY, tableX + contentWidth, tableY + itemsBoxHeight);

      // header row
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const headers = ["#", "PARTICULARS", "HSN Code", "GST Rate", "QTY", "RATE", "AMOUNT"];
      x = tableX;
      headers.forEach((h, i) => {
        doc.text(h, x + colWidths[i] / 2, tableY + 4.5, { align: "center" });
        x += colWidths[i];
      });
      doc.line(tableX, tableY + rowHeight, tableX + contentWidth, tableY + rowHeight);

      // merged row
      // one row per product
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);

      let rowY = tableY + rowHeight;

      // Maximum number of rows that fit inside itemsBoxHeight
      const maxRows = Math.floor((itemsBoxHeight - rowHeight) / rowHeight);

      // Render each item as its own row
      items.slice(0, maxRows).forEach((item, index) => {
        const rowIndex = index + 1;

        const name =
          getItemProperty(item, "name", "product_name", "productName", "title") ||
          "Unknown Product";

        const hsn =
          item.hsnCode || item.hsn_code || item.hsn || "";

        const qty = Number(
          getItemProperty(item, "qty", "quantity", "count") || 1
        );

        const price = Number(
          getItemProperty(item, "price", "base_price", "basePrice", "unitPrice") || 0
        );

        const itemSubtotalValue = getItemProperty(item, "subtotal");
        const amount = itemSubtotalValue
          ? Number(itemSubtotalValue)
          : price * qty;

        // Prefer item GST rate; fall back to overall gstPercentage; else 18%
        const perItemGstRate =
          Number(getItemProperty(item, "gstRate", "gst_rate")) ||
          (typeof gstPercentage === "number" && gstPercentage > 0
            ? gstPercentage
            : 18);

        const row = [
          rowIndex,
          name,
          hsn,
          `${perItemGstRate} %`,
          qty,
          formatAmountForPdf(price),
          formatAmountForPdf(amount),
        ];

        let cx = tableX;
        row.forEach((val, j) => {
          const align = j === 1 ? "left" : "center";
          const offsetX = j === 1 ? 2 : colWidths[j] / 2;
          doc.text(String(val), cx + offsetX, rowY + 4.5, { align });
          cx += colWidths[j];
        });

        rowY += rowHeight;
      });

      // Note: if items.length > maxRows, extra items are not printed.
      // You can extend this logic to a second page if needed.

      /* =========================
         BOTTOM SECTION
      ========================== */
      const bottomStartY = tableY + itemsBoxHeight;

      // Define section widths
      const leftSectionWidth = contentWidth * 0.55;
      const rightSectionX = marginX + leftSectionWidth;
      const rightSectionWidth = contentWidth - leftSectionWidth;
      const amountColWidth = 28;
      const amountColX = marginX + contentWidth - amountColWidth;

      // Calculate totals section height
      const totalsAreaHeight = 48; // Reduced slightly for tighter spacing
      const totalsEndY = bottomStartY + totalsAreaHeight;
      const signatureStartY = totalsEndY;
      const rightBoxBottom = bottomBoxY + bottomBoxHeight;

      // Calculation values
      const roundedTotal = Math.round(grandTotal);
      const words = numberToWordsIndian(roundedTotal);
      const cgst = gstAmount / 2 || 0;
      const sgst = gstAmount / 2 || 0;
      const gstRateHalf =
        typeof gstPercentage === "number" && gstPercentage > 0
          ? (gstPercentage / 2).toFixed(0)
          : "9";
      const roundOffDiff = roundedTotal - grandTotal;
      const roundOffAbs = Math.abs(roundOffDiff);
      const roundOffStr = (roundOffDiff >= 0 ? "" : "-") + formatAmountForPdf(roundOffAbs);

      /* --- RUPEES IN WORDS --- */
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Rupees ${words} Only.`, marginX + 2, bottomStartY + 5);

      // Horizontal line under "Rupees..."
      const rupeesLineY = bottomStartY + 8;
      doc.line(marginX, rupeesLineY, rightSectionX, rupeesLineY);

      /* --- LEFT SECTION: BANK DETAILS --- */
      let bankY = rupeesLineY + 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("BANK DETAILS :", marginX + 2, bankY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      bankY += 5;

      const bankDetails = [
        "NAME : SHUBHA GRAPHICS",
        "BANK : BANK OF BARODA , KARKALA",
        "O/D A/c.NO : 8201020000037",
        "IFSC CODE : BARB0VJKARK",
        "",
        "NAME : SHUBHA GRAPHICS",
        "BANK : CANARA BANK , KARKALA",
        "C/A NO : 02663070000434",
        "IFSC CODE : CNRB0010114",
      ];

      bankDetails.forEach((line) => {
        doc.text(line, marginX + 2, bankY);
        bankY += 4.5;
      });

      /* --- RIGHT SECTION: TOTALS AREA (TIGHTER SPACING) --- */
      // Vertical line separating left and right
      doc.line(rightSectionX, bottomStartY, rightSectionX, rightBoxBottom);

      // Amount column vertical line - ONLY in totals area
      doc.line(amountColX, bottomStartY, amountColX, totalsEndY);

      // Labels and amounts
      const labelX = rightSectionX + 3;
      const amountRightX = marginX + contentWidth - 2;

      doc.setFontSize(9);
      let ty = bottomStartY + 5;

      // Total row
      doc.setFont("helvetica", "bold");
      doc.text("Total", labelX, ty);
      doc.setFont("helvetica", "normal");
      doc.text(formatAmountForPdf(subtotal), amountRightX, ty, { align: "right" });
      ty += 6; // Reduced from 7

      // CGST row
      doc.text(`Output CGST @ ${gstRateHalf}%`, labelX, ty);
      doc.text(formatAmountForPdf(cgst), amountRightX, ty, { align: "right" });
      ty += 6; // Reduced from 7

      // SGST row
      doc.text(`Output SGST @ ${gstRateHalf}%`, labelX, ty);
      doc.text(formatAmountForPdf(sgst), amountRightX, ty, { align: "right" });
      ty += 3; // Reduced from 4

      // Horizontal divider after SGST
      doc.line(rightSectionX, ty, marginX + contentWidth, ty);
      ty += 4; // Reduced from 5

      // Grand Total row
      doc.setFont("helvetica", "bold");
      doc.text("Grand Total", labelX, ty);
      doc.setFont("helvetica", "normal");
      doc.text(formatAmountForPdf(grandTotal), amountRightX, ty, { align: "right" });
      ty += 6; // Reduced from 7

      // Round Off row
      doc.text("Round Off", labelX, ty);
      doc.text(roundOffStr, amountRightX, ty, { align: "right" });
      ty += 3; // Reduced from 4

      // Horizontal line before Invoice Amount
      doc.line(rightSectionX, ty, marginX + contentWidth, ty);
      ty += 4; // Reduced from 5

      // Invoice Amount row
      doc.setFont("helvetica", "bold");
      doc.text("Invoice Amount", labelX, ty);
      doc.text(formatAmountForPdf(roundedTotal), amountRightX, ty, { align: "right" });

      /* --- SIGNATURE SECTION --- */
      // Horizontal line separating totals from signature
      doc.setLineWidth(0.2);
      doc.line(rightSectionX, totalsEndY, marginX + contentWidth, totalsEndY);

      // Calculate signature section dimensions
      const sigCenterX = rightSectionX + rightSectionWidth / 2;

      // "For SHUBHA GRAPHICS" - positioned near top of signature area
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("For SHUBHA GRAPHICS", sigCenterX, signatureStartY + 8, { align: "center" });

      // Space for seal image (uncomment and add your seal)
      await new Promise((resolve) => {
        const sealImg = new Image();
        sealImg.src = seal;
        sealImg.onload = () => {
          const sealWidth = 30;
          const sealHeight = 25;
          const sealX = sigCenterX - sealWidth / 2;
          const sealY = signatureStartY + 12;
          doc.addImage(sealImg, "PNG", sealX, sealY, sealWidth, sealHeight);
          resolve();
        };
        sealImg.onerror = resolve;
      });

      // "Authorised Signatory" - positioned near bottom of signature area
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Authorised Signatory", sigCenterX, rightBoxBottom - 4, { align: "center" });

      doc.save(`invoice-${effectiveInvoiceNo}.pdf`);
      addToast("Invoice downloaded!", "success");
    } catch (err) {
      console.error(err);
      addToast("Failed to generate invoice", "error");
    }
  };

  return (
    <div className="orders-page order-details-page">
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header */}
      <div className="order-details-header">
        <div className="order-details-header-top">
          <div className="order-details-header-left">
            <h1 className="order-details-title">Order Details</h1>
            <div className="order-details-breadcrumbs">
              <span className="breadcrumb-link">Dashboard</span>
              <FiChevronRight className="breadcrumb-separator-icon" />
              <button
                type="button"
                className="breadcrumb-link breadcrumb-button"
                onClick={onBack}
              >
                Order List
              </button>
              <FiChevronRight className="breadcrumb-separator-icon" />
              <span className="breadcrumb-current">Order Details</span>
            </div>
          </div>

          <div className="order-details-header-right">
            <div className="order-details-status-select">
              <div className="status-color-dot" style={{ backgroundColor: currentStatusColor }} />
              <select
                value={statusKey}
                onChange={handleStatusChange}
                disabled={updatingStatus}
                className={`status-select status-select-${statusKey}`}
              >
                {STATUS_STEPS.map((step) => (
                  <option key={step.key} value={step.key}>
                    {step.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="status-select-icon" />
              {updatingStatus && (
                <span className="status-updating-indicator">
                  <span className="spinner-small"></span>
                  Updating...
                </span>
              )}
            </div>

            <div className="order-details-status-select">
              <div
                className="status-color-dot"
                style={{ backgroundColor: currentPaymentStatusColor }}
              />
              <select
                value={paymentStatusKey}
                onChange={handlePaymentStatusChange}
                disabled={updatingPaymentStatus}
                className={`status-select status-select-${paymentStatusKey}`}
              >
                {PAYMENT_STATUS_STEPS.map((step) => (
                  <option key={step.key} value={step.key}>
                    {step.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="status-select-icon" />
              {updatingPaymentStatus && (
                <span className="status-updating-indicator">
                  <span className="spinner-small"></span>
                  Updating...
                </span>
              )}
            </div>

            {/* New: Invoice number input */}
            <div className="invoice-number-field">
              <label htmlFor="invoiceNumber">Invoice No.</label>
              <input
                id="invoiceNumber"
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Enter invoice no."
              />
            </div>
            {/* NEW: Invoice date input */}
            <div className="invoice-number-field">
              <label htmlFor="invoiceDate">Date</label>
              <input
                id="invoiceDate"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>

            <button type="button" className="btn btn-outline" onClick={handleExportOrder}>
              <BiExport className="btn-icon" />
              <span>Export</span>
            </button>

            <button type="button" className="btn btn-primary" onClick={handleInvoice}>
              <FiFileText className="btn-icon" />
              <span>Invoice</span>
            </button>
          </div>
        </div>
      </div>

      {/* Body Layout */}
      <div className="order-details-layout">
        {/* Left Column */}
        <div className="order-details-left">
          {/* Customer Card */}
          <div className="order-details-card">
            <h2 className="order-details-card-title">Customer</h2>
            <div className="customer-summary">
              <div className="customer-summary-row">
                <span className="customer-summary-label">Name:</span>
                <span className="customer-summary-value">{customerName}</span>
              </div>
              <div className="customer-summary-row">
                <span className="customer-summary-label">Email:</span>
                <span className="customer-summary-value">{customerEmail}</span>
              </div>
              <div className="customer-summary-row">
                <span className="customer-summary-label">Phone:</span>
                <span className="customer-summary-value">{customerPhone}</span>
              </div>
              <div className="customer-summary-row">
                <span className="customer-summary-label">Pin Code:</span>
                <span className="customer-summary-value">{customerPincode}</span>
              </div>
            </div>
          </div>

          {/* Order Items Card */}
          <div className="orderlist-details-card">
            <div className="order-details-card-header">
              <h2 className="order-details-card-title">Order List</h2>
              <span className="product-count-pill">
                {productsCount} {productsCount === 1 ? "Product" : "Products"}
              </span>
            </div>

            <div className="orders-table-wrapper order-details-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th className="col-product">Product</th>
                    <th className="col-sku">SKU</th>
                    <th className="col-qty">QTY</th>
                    <th className="col-price">Price</th>
                    <th className="col-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-table-cell">
                        No products in this order.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {items.map((item, index) => {
                        const itemName = getItemProperty(item, 'name', 'product_name', 'productName', 'title') || "Unknown Product";
                        const itemSku = getItemProperty(item, 'sku', 'productSku') || "N/A";
                        const itemQty = Number(getItemProperty(item, 'qty', 'quantity', 'count') || 1);
                        const itemPrice = Number(getItemProperty(item, 'price', 'base_price', 'basePrice', 'unitPrice') || 0);
                        const itemSubtotalValue = getItemProperty(item, 'subtotal');
                        const itemTotal = itemSubtotalValue ? Number(itemSubtotalValue) : itemPrice * itemQty;
                        const itemImage = findImageUrl(item);

                        return (
                          <tr key={`${itemSku}-${index}`} className="order-item-row">
                            <td className="col-product" data-label="Product">
                              <div className="product-cell">
                                {itemImage ? (
                                  <img
                                    src={itemImage}
                                    alt={itemName}
                                    className="product-image"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = "flex";
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="product-image-placeholder"
                                  style={{ display: itemImage ? "none" : "flex" }}
                                >
                                  <FiPackage size={20} />
                                </div>
                                <div className="product-info">
                                  <div className="product-name">{itemName}</div>
                                </div>
                              </div>
                            </td>
                            <td className="col-sku" data-label="SKU">{itemSku}</td>
                            <td className="col-qty" data-label="QTY">{itemQty} pcs</td>
                            <td className="col-price" data-label="Price">{formatINR(itemPrice)}</td>
                            <td className="col-total" data-label="Total">{formatINR(itemTotal)}</td>
                          </tr>
                        );
                      })}

                      {/* ====== SUMMARY ROWS ====== */}

                      {/* Subtotal */}
                      <tr className="order-items-summary-row subtotal-row">
                        <td colSpan={4} className="order-items-summary-label">Subtotal</td>
                        <td className="order-items-summary-value">{formatINR(subtotal)}</td>
                      </tr>

                      {/* GST Row - Only show if GST > 0 */}
                      {gstAmount > 0 && (
                        <tr className="order-items-summary-row tax-row gst-row">
                          <td colSpan={4} className="order-items-summary-label">
                            GST {typeof gstPercentage === 'number' && gstPercentage > 0
                              ? `(${gstPercentage}%)`
                              : gstPercentage === 'Mixed' ? '(Mixed Rates)' : ''}
                          </td>
                          <td className="order-items-summary-value">{formatINR(gstAmount)}</td>
                        </tr>
                      )}

                      {/* VAT Row - Only show if VAT > 0 */}
                      {vatAmount > 0 && (
                        <tr className="order-items-summary-row tax-row vat-row">
                          <td colSpan={4} className="order-items-summary-label">
                            VAT {typeof vatPercentage === 'number' && vatPercentage > 0
                              ? `(${vatPercentage}%)`
                              : vatPercentage === 'Mixed' ? '(Mixed Rates)' : ''}
                          </td>
                          <td className="order-items-summary-value">{formatINR(vatAmount)}</td>
                        </tr>
                      )}

                      {/* Shipping Row - Only show if shipping > 0 */}
                      {shippingRate > 0 && (
                        <tr className="order-items-summary-row shipping-row">
                          <td colSpan={4} className="order-items-summary-label">Shipping</td>
                          <td className="order-items-summary-value">{formatINR(shippingRate)}</td>
                        </tr>
                      )}

                      {/* Grand Total - Always at bottom */}
                      <tr className="order-items-summary-row grand-total-row">
                        <td colSpan={4} className="order-items-summary-label">Grand Total</td>
                        <td className="order-items-summary-value">{formatINR(grandTotal)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="order-details-right">
          {/* Order Status Card */}
          <div className="order-details-card">
            <h2 className="order-details-card-title">Order Status</h2>
            <div className="status-list">
              {STATUS_STEPS.map((step, index) => {
                const isDone = index < activeIndex;
                const isActive = index === activeIndex;
                const stateClass = isDone
                  ? "status-item--done"
                  : isActive
                    ? "status-item--active"
                    : "status-item--pending";

                return (
                  <div key={step.key} className={`status-item ${stateClass}`}>
                    <div className="status-icon-wrapper">
                      <div
                        className="status-circle"
                        style={{
                          backgroundColor: isDone || isActive ? step.color : '#e5e7eb',
                          borderColor: step.color
                        }}
                      >
                        {isDone && <FiCheck size={12} color="white" />}
                        {isActive && !isDone && <FiClock size={12} color="white" />}
                      </div>
                      {index < STATUS_STEPS.length - 1 && (
                        <div
                          className={`status-line ${isDone ? "status-line--done" : ""}`}
                          style={{ backgroundColor: isDone ? step.color : '#e5e7eb' }}
                        />
                      )}
                    </div>
                    <div className="status-content">
                      <div
                        className="status-title"
                        style={{ color: isActive ? step.color : undefined, fontWeight: isActive ? 600 : 400 }}
                      >
                        {step.label}
                      </div>

                      <div className="status-description">{step.description}</div>

                      {statusUsers[step.key] && (
                        <div className="status-updated-by">
                          Updated by: <strong>{statusUsers[step.key]}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payment Status Card */}
          <div className="order-details-card">
            <h2 className="order-details-card-title">Payment Status</h2>
            <div className="status-list">
              {PAYMENT_STATUS_STEPS.map((step, index) => {
                const isDone = index < paymentActiveIndex;
                const isActive = index === paymentActiveIndex;
                const stateClass = isDone
                  ? "status-item--done"
                  : isActive
                    ? "status-item--active"
                    : "status-item--pending";

                return (
                  <div key={step.key} className={`status-item ${stateClass}`}>
                    <div className="status-icon-wrapper">
                      <div
                        className="status-circle"
                        style={{
                          backgroundColor: isDone || isActive ? step.color : "#e5e7eb",
                          borderColor: step.color,
                        }}
                      >
                        {isDone && <FiCheck size={12} color="white" />}
                        {isActive && !isDone && <FiClock size={12} color="white" />}
                      </div>
                      {index < PAYMENT_STATUS_STEPS.length - 1 && (
                        <div
                          className={`status-line ${isDone ? "status-line--done" : ""}`}
                          style={{ backgroundColor: isDone ? step.color : "#e5e7eb" }}
                        />
                      )}
                    </div>
                    <div className="status-content">
                      <div
                        className="status-title"
                        style={{
                          color: isActive ? step.color : undefined,
                          fontWeight: isActive ? 600 : 400,
                        }}
                      >
                        {step.label}
                      </div>

                      <div className="status-description">{step.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Address Card */}
          <div className="order-details-card">
            <h2 className="order-details-card-title">Address</h2>
            <div className="address-row">
              <div className="address-icon"><FiMapPin /></div>
              <div className="address-content">
                <div className="address-label">Shipping</div>
                <div className="address-text">{shippingAddress}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;